import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Chip,
  Checkbox,
  ListItemText,
  CircularProgress,
  Alert as MuiAlert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ToggleButton,
  ToggleButtonGroup,
  OutlinedInput,
  Tabs,
  Tab
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Download as DownloadIcon,
  AttachFile as AttachFileIcon
} from '@mui/icons-material';
import AasChatPanel from './aas/AasChatPanel';
import InputAdornment from '@mui/material/InputAdornment';
import { format } from 'date-fns';
import { getTickets, getTicketDetail, updateTicket, addTicketNote, getTicketAttachment } from '../../services/supportTicketApi';

/** Human-readable file size. The API reports exact decoded bytes. */
const formatBytes = (n) => {
  if (!Number.isFinite(n)) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1048576) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1048576).toFixed(1)} MB`;
};

const STATUS_OPTIONS = [
  { value: 'open', label: 'Open', color: '#2196f3' },
  { value: 'in_progress', label: 'In Progress', color: '#ff9800' },
  { value: 'waiting_on_user', label: 'Waiting on User', color: '#ffc107' },
  { value: 'resolved', label: 'Resolved', color: '#4caf50' },
  { value: 'closed', label: 'Closed', color: '#9e9e9e' }
];

const STATUS_COLORS = Object.fromEntries(STATUS_OPTIONS.map(s => [s.value, s.color]));
const STATUS_LABELS = Object.fromEntries(STATUS_OPTIONS.map(s => [s.value, s.label]));

const PRIORITY_COLORS = { low: '#66bb6a', medium: '#42a5f5', high: '#ffa726', urgent: '#d32f2f' };
const PRIORITY_LABELS = { low: 'Low', medium: 'Medium', high: 'High', urgent: 'Urgent' };

/**
 * Flatten ticket.context into [label, value, options] rows, in display order.
 * Single source for the detail chips and the .md / .txt exports so a new
 * context field only has to be added once. Rows with an empty value are
 * omitted. `emphasis` marks rows worth colouring in the chip view.
 */
const getContextRows = (context) => {
  if (!context) return [];
  const c = context;
  const client = c.viewingClientId;
  const clientLabel = client && typeof client === 'object'
    ? `${client.name || client.email || client._id}${client.email && client.name ? ` (${client.email})` : ''}`
    : client;
  const bool = (v) => (v == null ? null : v ? 'On' : 'Off');

  const rows = [
    ['Reported by', c.userRole, { emphasis: c.userRole === 'therapist' }],
    ['Location', c.uiLocation, { emphasis: true }],
    ['Page', c.currentPage],
    ['Stage', c.journeyStage, { emphasis: c.journeyStage === 'training' }],
    ['Dashboard tab', c.dashboardTab],
    ['Client tab', c.clientTab],
    ['Client in view', clientLabel, { emphasis: true }],
    ['Client kind', c.viewingClientKind, { emphasis: c.viewingClientKind === 'synthetic' }],
    ['Persona', c.personaKey],
    ['Conversation', c.conversationId],
    ['Control mode', c.conversationControlMode],
    ['AI consultation', c.aiConsultationId],
    ['Live call', c.liveCallId],
    ['Call state', c.callConnectionState],
    ['Call quality', c.callConnectionQuality],
    ['Mic', bool(c.micEnabled)],
    ['Camera', bool(c.cameraEnabled)],
    ['Training session', c.trainingSessionId],
    ['Scenario ID', c.scenarioId],
    ['Scenario', c.scenarioName, { emphasis: true }],
    ['Setting', c.scenarioSetting],
    ['Turns', c.totalTurns],
    ['Assessments used', c.assessmentsUsed?.length ? c.assessmentsUsed.join(', ') : null],
    ['Voice mode', c.voiceModeActive == null ? null : (c.voiceModeActive ? 'Active' : 'Inactive')],
    ['Route', c.route],
    ['Screen', c.screenSize],
    ['Browser', c.userAgent, { longText: true }]
  ];
  return rows
    .filter(([, value]) => value !== undefined && value !== null && value !== '')
    .map(([label, value, opts = {}]) => [label, String(value), opts]);
};

const ISSUE_TYPE_LABELS = {
  technical_problem: 'Technical Problem',
  something_doesnt_feel_right: "Something Doesn't Feel Right",
  general_feedback: 'General Feedback',
  feature_request: 'Feature Request'
};

// Default: show everything except closed
const DEFAULT_STATUSES = ['open', 'in_progress', 'waiting_on_user', 'resolved'];

const SupportTicketsDashboard = ({ token }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);

  // Filters
  const [statusFilter, setStatusFilter] = useState(DEFAULT_STATUSES);
  const [priorityFilter, setPriorityFilter] = useState('');
  const [issueTypeFilter, setIssueTypeFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [searchText, setSearchText] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const searchTimerRef = useRef(null);

  // Sorting
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState(-1); // -1 = desc, 1 = asc

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Detail modal
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Update controls
  const [statusUpdate, setStatusUpdate] = useState('');
  const [priorityUpdate, setPriorityUpdate] = useState('');
  const [updateLoading, setUpdateLoading] = useState(false);

  // Note form
  const [noteText, setNoteText] = useState('');
  const [noteType, setNoteType] = useState('internal');
  const [noteLoading, setNoteLoading] = useState(false);

  // Detail tabs (0 = Details, 1 = AI Investigation)
  const [detailTab, setDetailTab] = useState(0);

  // Attachment. The ticket carries the metadata; the bytes are fetched
  // separately so the list and detail payloads stay small (ST-20260921-0002).
  const [attachment, setAttachment] = useState(null);
  const [attachmentLoading, setAttachmentLoading] = useState(false);
  const [attachmentError, setAttachmentError] = useState(null);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filters = {};
      if (statusFilter.length > 0 && statusFilter.length < STATUS_OPTIONS.length) {
        filters.status = statusFilter.join(',');
      }
      if (priorityFilter) filters.priority = priorityFilter;
      if (issueTypeFilter) filters.issueType = issueTypeFilter;
      if (searchText.trim()) filters.search = searchText.trim();
      if (dateFrom) filters.dateFrom = dateFrom;
      if (dateTo) filters.dateTo = dateTo;
      filters.sortBy = sortBy;
      filters.sortOrder = sortOrder;
      filters.limit = rowsPerPage;
      filters.offset = page * rowsPerPage;

      const data = await getTickets(filters, token);
      setTickets(data.tickets || []);
      setTotal(data.total || 0);
    } catch (err) {
      setError(err.response?.data?.msg || err.message || 'Failed to load tickets.');
    } finally {
      setLoading(false);
    }
  }, [token, statusFilter, priorityFilter, issueTypeFilter, searchText, dateFrom, dateTo, sortBy, sortOrder, page, rowsPerPage]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  // Reset to first page when filters change
  useEffect(() => {
    setPage(0);
  }, [statusFilter, priorityFilter, issueTypeFilter, searchText, dateFrom, dateTo]);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(prev => prev === -1 ? 1 : -1);
    } else {
      setSortBy(column);
      setSortOrder(-1);
    }
  };

  const loadAttachment = useCallback(async (ticketId) => {
    setAttachmentLoading(true);
    setAttachmentError(null);
    try {
      // null means the ticket has no attachment, which is ordinary, not a failure.
      setAttachment(await getTicketAttachment(ticketId, token));
    } catch (err) {
      // The report itself is still readable, so this degrades to a message next
      // to the file rather than replacing the whole ticket with an error.
      setAttachmentError('The attachment could not be loaded.');
    } finally {
      setAttachmentLoading(false);
    }
  }, [token]);

  const handleRowClick = async (ticketId) => {
    setDetailLoading(true);
    setDetailTab(0); // Reset to Details tab when opening a new ticket
    setAttachment(null);
    setAttachmentError(null);
    try {
      const data = await getTicketDetail(ticketId, token);
      setSelectedTicket(data.ticket);
      setStatusUpdate(data.ticket.status);
      setPriorityUpdate(data.ticket.priority);
      // Fetched straight away when there is one: an admin opening a ticket that
      // has a screenshot is opening it to look at the screenshot. Only ever one
      // file, and only for the ticket actually being read.
      if (data.ticket.attachment?.filename) {
        loadAttachment(ticketId);
      }
    } catch (err) {
      setError('Failed to load ticket detail.');
    } finally {
      setDetailLoading(false);
    }
  };

  const downloadAttachment = () => {
    if (!attachment?.bytes) return;
    const binary = atob(attachment.bytes);
    const arr = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) arr[i] = binary.charCodeAt(i);
    const blob = new Blob([arr], { type: attachment.contentType || 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = attachment.filename || 'attachment';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleUpdateTicket = async () => {
    if (!selectedTicket) return;
    setUpdateLoading(true);
    try {
      const updates = {};
      if (statusUpdate !== selectedTicket.status) updates.status = statusUpdate;
      if (priorityUpdate !== selectedTicket.priority) updates.priority = priorityUpdate;

      if (Object.keys(updates).length === 0) {
        setUpdateLoading(false);
        return;
      }

      const data = await updateTicket(selectedTicket._id, updates, token);
      setSelectedTicket(data.ticket);
      fetchTickets();
    } catch (err) {
      setError('Failed to update ticket.');
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!selectedTicket || !noteText.trim()) return;
    setNoteLoading(true);
    try {
      const data = await addTicketNote(selectedTicket._id, { text: noteText.trim(), type: noteType }, token);
      setSelectedTicket(data.ticket);
      setNoteText('');
    } catch (err) {
      setError('Failed to add note.');
    } finally {
      setNoteLoading(false);
    }
  };

  const handleCloseDetail = () => {
    setSelectedTicket(null);
    setNoteText('');
    setNoteType('internal');
    setDetailTab(0);
    // Without this the previous ticket's screenshot is still on screen for a
    // moment when the next one opens, which would misattribute it.
    setAttachment(null);
    setAttachmentError(null);
  };

  const handleStatusFilterChange = (event) => {
    const val = event.target.value;
    setStatusFilter(typeof val === 'string' ? val.split(',') : val);
  };

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchInput(val);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => setSearchText(val), 400);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearchText('');
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
  };

  const downloadTicket = (fmt) => {
    if (!selectedTicket) return;
    const t = selectedTicket;
    const fmtDate = (d) => d ? format(new Date(d), 'dd MMM yyyy HH:mm:ss') : '—';
    const sep = fmt === 'md' ? '\n\n' : '\n\n';
    const hr = fmt === 'md' ? '---' : '────────────────────────────────';

    let content = '';
    if (fmt === 'md') {
      content += `# ${t.ticketNumber}\n\n`;
      content += `| Field | Value |\n|-------|-------|\n`;
      content += `| **Status** | ${STATUS_LABELS[t.status] || t.status} |\n`;
      content += `| **Priority** | ${PRIORITY_LABELS[t.priority] || t.priority} |\n`;
      content += `| **Issue Type** | ${ISSUE_TYPE_LABELS[t.issueType] || t.issueType} |\n`;
      content += `| **User** | ${t.userId?.name || '—'} (${t.userId?.email || t.userEmail}) |\n`;
      content += `| **Created** | ${fmtDate(t.createdAt)} |\n`;
      content += `| **Updated** | ${fmtDate(t.updatedAt)} |\n`;
      if (t.resolvedAt) content += `| **Resolved** | ${fmtDate(t.resolvedAt)} |\n`;
      if (t.closedAt) content += `| **Closed** | ${fmtDate(t.closedAt)} |\n`;
      content += sep;

      const contextRows = getContextRows(t.context);
      if (contextRows.length) {
        content += `## Context\n\n`;
        contextRows.forEach(([label, value]) => { content += `- **${label}:** ${value}\n`; });
        content += sep;
      }

      content += `## Description\n\n${t.description}\n`;

      if (t.notes && t.notes.length > 0) {
        content += `${sep}## Notes (${t.notes.length})\n\n`;
        t.notes.forEach((note) => {
          content += `${hr}\n\n`;
          content += `**${note.authorName || 'Staff'}** — _${note.type}_ — ${fmtDate(note.createdAt)}\n\n`;
          content += `${note.text}\n\n`;
        });
      }
    } else {
      // Plain text
      content += `TICKET: ${t.ticketNumber}\n`;
      content += `${hr}\n\n`;
      content += `Status:     ${STATUS_LABELS[t.status] || t.status}\n`;
      content += `Priority:   ${PRIORITY_LABELS[t.priority] || t.priority}\n`;
      content += `Issue Type: ${ISSUE_TYPE_LABELS[t.issueType] || t.issueType}\n`;
      content += `User:       ${t.userId?.name || '—'} (${t.userId?.email || t.userEmail})\n`;
      content += `Created:    ${fmtDate(t.createdAt)}\n`;
      content += `Updated:    ${fmtDate(t.updatedAt)}\n`;
      if (t.resolvedAt) content += `Resolved:   ${fmtDate(t.resolvedAt)}\n`;
      if (t.closedAt) content += `Closed:     ${fmtDate(t.closedAt)}\n`;
      content += sep;

      const contextRows = getContextRows(t.context);
      if (contextRows.length) {
        content += `CONTEXT\n${hr}\n`;
        const pad = Math.max(...contextRows.map(([label]) => label.length)) + 2;
        contextRows.forEach(([label, value]) => { content += `${`${label}:`.padEnd(pad)}${value}\n`; });
        content += sep;
      }

      content += `DESCRIPTION\n${hr}\n${t.description}\n`;

      if (t.notes && t.notes.length > 0) {
        content += `${sep}NOTES (${t.notes.length})\n${hr}\n\n`;
        t.notes.forEach((note) => {
          content += `[${note.type.toUpperCase()}] ${note.authorName || 'Staff'} — ${fmtDate(note.createdAt)}\n`;
          content += `${note.text}\n\n`;
        });
      }
    }

    const ext = fmt === 'md' ? 'md' : 'txt';
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${t.ticketNumber}.${ext}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const sortableColumns = [
    { id: 'ticketNumber', label: 'Ticket #' },
    { id: 'userEmail', label: 'User', sortable: false },
    { id: 'issueType', label: 'Issue Type' },
    { id: 'status', label: 'Status' },
    { id: 'priority', label: 'Priority' },
    { id: 'createdAt', label: 'Created' },
    { id: 'updatedAt', label: 'Updated' }
  ];

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Support Tickets</Typography>
        <Button startIcon={<RefreshIcon />} onClick={fetchTickets} disabled={loading}>
          Refresh
        </Button>
      </Box>

      {error && <MuiAlert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</MuiAlert>}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        {/* Row 1: Search + dropdowns */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 1.5 }}>
          <TextField
            size="small"
            placeholder="Search tickets..."
            value={searchInput}
            onChange={handleSearchChange}
            sx={{ minWidth: 240 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><SearchIcon fontSize="small" color="action" /></InputAdornment>
              ),
              endAdornment: searchInput ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleClearSearch}><ClearIcon fontSize="small" /></IconButton>
                </InputAdornment>
              ) : null
            }}
          />
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Status</InputLabel>
            <Select
              multiple
              value={statusFilter}
              onChange={handleStatusFilterChange}
              input={<OutlinedInput label="Status" />}
              renderValue={(selected) => selected.map(s => STATUS_LABELS[s]).join(', ')}
            >
              {STATUS_OPTIONS.map(({ value, label }) => (
                <MenuItem key={value} value={value}>
                  <Checkbox checked={statusFilter.includes(value)} size="small" />
                  <ListItemText primary={label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Priority</InputLabel>
            <Select value={priorityFilter} label="Priority" onChange={(e) => setPriorityFilter(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {Object.entries(PRIORITY_LABELS).map(([val, label]) => (
                <MenuItem key={val} value={val}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Issue Type</InputLabel>
            <Select value={issueTypeFilter} label="Issue Type" onChange={(e) => setIssueTypeFilter(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {Object.entries(ISSUE_TYPE_LABELS).map(([val, label]) => (
                <MenuItem key={val} value={val}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="body2" sx={{ alignSelf: 'center', color: 'text.secondary', ml: 'auto' }}>
            {total} ticket{total !== 1 ? 's' : ''}
          </Typography>
        </Box>
        {/* Row 2: Date range */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            label="From"
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 160 }}
          />
          <TextField
            size="small"
            label="To"
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 160 }}
          />
          {(dateFrom || dateTo) && (
            <Button size="small" onClick={() => { setDateFrom(''); setDateTo(''); }}>
              Clear dates
            </Button>
          )}
        </Box>
      </Paper>

      {/* Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      ) : (
        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {sortableColumns.map(col => (
                    <TableCell key={col.id}>
                      {col.sortable === false ? col.label : (
                        <TableSortLabel
                          active={sortBy === col.id}
                          direction={sortBy === col.id ? (sortOrder === -1 ? 'desc' : 'asc') : 'desc'}
                          onClick={() => handleSort(col.id)}
                        >
                          {col.label}
                        </TableSortLabel>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {tickets.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography color="text.secondary" sx={{ py: 2 }}>No tickets found.</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  tickets.map(ticket => (
                    <TableRow
                      key={ticket._id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleRowClick(ticket._id)}
                    >
                      <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{ticket.ticketNumber}</TableCell>
                      <TableCell>{ticket.userId?.email || ticket.userEmail || '—'}</TableCell>
                      <TableCell>{ISSUE_TYPE_LABELS[ticket.issueType] || ticket.issueType}</TableCell>
                      <TableCell>
                        <Chip
                          label={STATUS_LABELS[ticket.status] || ticket.status}
                          size="small"
                          sx={{ bgcolor: STATUS_COLORS[ticket.status] || '#ccc', color: '#fff', fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={PRIORITY_LABELS[ticket.priority] || ticket.priority}
                          size="small"
                          variant="outlined"
                          sx={{ borderColor: PRIORITY_COLORS[ticket.priority], color: PRIORITY_COLORS[ticket.priority], fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{format(new Date(ticket.createdAt), 'dd MMM yyyy HH:mm:ss')}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{format(new Date(ticket.updatedAt), 'dd MMM yyyy HH:mm:ss')}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </Paper>
      )}

      {/* Detail Dialog */}
      <Dialog open={!!selectedTicket} onClose={handleCloseDetail} maxWidth="md" fullWidth>
        {selectedTicket && (
          <>
            <DialogTitle>
              Ticket {selectedTicket.ticketNumber}
              <IconButton
                onClick={handleCloseDetail}
                sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ p: 0 }}>
              {detailLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>
              ) : (
                <>
                  <Tabs
                    value={detailTab}
                    onChange={(e, v) => setDetailTab(v)}
                    sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
                  >
                    <Tab label="Details" />
                    <Tab label="AI Investigation" />
                  </Tabs>

                  {detailTab === 1 && (
                    <AasChatPanel ticketId={selectedTicket._id} token={token} />
                  )}

                  {detailTab === 0 && (
                  <Box sx={{ p: 2 }}>
                  {/* Timestamps */}
                  <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">Created</Typography>
                      <Typography variant="body2">{format(new Date(selectedTicket.createdAt), 'dd MMM yyyy HH:mm:ss')}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">Last Updated</Typography>
                      <Typography variant="body2">{format(new Date(selectedTicket.updatedAt), 'dd MMM yyyy HH:mm:ss')}</Typography>
                    </Box>
                    {selectedTicket.resolvedAt && (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">Resolved</Typography>
                        <Typography variant="body2">{format(new Date(selectedTicket.resolvedAt), 'dd MMM yyyy HH:mm:ss')}</Typography>
                      </Box>
                    )}
                    {selectedTicket.closedAt && (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">Closed</Typography>
                        <Typography variant="body2">{format(new Date(selectedTicket.closedAt), 'dd MMM yyyy HH:mm:ss')}</Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Current Status & Priority */}
                  <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                    <Chip
                      label={STATUS_LABELS[selectedTicket.status] || selectedTicket.status}
                      sx={{ bgcolor: STATUS_COLORS[selectedTicket.status] || '#ccc', color: '#fff', fontWeight: 600 }}
                    />
                    <Chip
                      label={PRIORITY_LABELS[selectedTicket.priority] || selectedTicket.priority}
                      variant="outlined"
                      sx={{ borderColor: PRIORITY_COLORS[selectedTicket.priority], color: PRIORITY_COLORS[selectedTicket.priority], fontWeight: 600 }}
                    />
                  </Box>

                  {/* User */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" color="text.secondary">User</Typography>
                    <Typography>{selectedTicket.userId?.name || '—'} ({selectedTicket.userId?.email || selectedTicket.userEmail})</Typography>
                  </Box>

                  {/* Issue Type */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" color="text.secondary">Issue Type</Typography>
                    <Typography>{ISSUE_TYPE_LABELS[selectedTicket.issueType] || selectedTicket.issueType}</Typography>
                  </Box>

                  {/* Description */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" color="text.secondary">Description</Typography>
                    <Paper variant="outlined" sx={{ p: 2, whiteSpace: 'pre-wrap' }}>
                      {selectedTicket.description}
                    </Paper>
                  </Box>

                  {/* Attachment — shown in place, because the whole point of a
                      screenshot is being able to look at it (ST-20260921-0002). */}
                  {selectedTicket.attachment?.filename && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" color="text.secondary">Attachment</Typography>
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <AttachFileIcon fontSize="small" color="action" />
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {selectedTicket.attachment.filename}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatBytes(selectedTicket.attachment.size)}
                            {selectedTicket.attachment.contentType ? ` · ${selectedTicket.attachment.contentType}` : ''}
                          </Typography>
                          {attachmentLoading && <CircularProgress size={16} />}
                          {!attachment && !attachmentLoading && !attachmentError && (
                            <Button size="small" onClick={() => loadAttachment(selectedTicket._id)}>
                              Load
                            </Button>
                          )}
                          {attachment && (
                            <Button size="small" startIcon={<DownloadIcon />} onClick={downloadAttachment}>
                              Download
                            </Button>
                          )}
                        </Box>

                        {attachmentError && (
                          <MuiAlert severity="warning" sx={{ mt: 1 }}>
                            {attachmentError}
                          </MuiAlert>
                        )}

                        {attachment && (
                          <Box sx={{ mt: 2 }}>
                            {attachment.contentType?.startsWith('image/') ? (
                              <img
                                src={`data:${attachment.contentType};base64,${attachment.bytes}`}
                                alt={attachment.filename}
                                style={{ maxWidth: '100%', borderRadius: 4, border: '1px solid #ddd', display: 'block' }}
                              />
                            ) : attachment.contentType === 'text/plain' || attachment.contentType === 'text/csv' || attachment.contentType === 'application/json' ? (
                              <Paper
                                variant="outlined"
                                sx={{ p: 1.5, maxHeight: 320, overflow: 'auto', bgcolor: '#fafafa' }}
                              >
                                <Typography variant="caption" component="pre" sx={{ m: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                                  {(() => { try { return atob(attachment.bytes); } catch { return 'Could not decode this file.'; } })()}
                                </Typography>
                              </Paper>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                No preview for this file type — use Download.
                              </Typography>
                            )}
                          </Box>
                        )}
                      </Paper>
                    </Box>
                  )}

                  {/* Context */}
                  {getContextRows(selectedTicket.context).length > 0 && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" color="text.secondary">Context</Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                        {getContextRows(selectedTicket.context)
                          .filter(([, , opts]) => !opts.longText)
                          .map(([label, value, opts]) => (
                            <Chip
                              key={label}
                              size="small"
                              label={`${label}: ${value}`}
                              color={opts.emphasis ? 'secondary' : 'default'}
                              variant={opts.emphasis ? 'filled' : 'outlined'}
                            />
                          ))}
                      </Box>
                      {getContextRows(selectedTicket.context)
                        .filter(([, , opts]) => opts.longText)
                        .map(([label, value]) => (
                          <Typography key={label} variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, wordBreak: 'break-all' }}>
                            {label}: {value}
                          </Typography>
                        ))}
                    </Box>
                  )}

                  {/* Status / Priority Update */}
                  <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>Update Ticket</Typography>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                      <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel>Status</InputLabel>
                        <Select value={statusUpdate} label="Status" onChange={(e) => setStatusUpdate(e.target.value)}>
                          {Object.entries(STATUS_LABELS).map(([val, label]) => (
                            <MenuItem key={val} value={val}>{label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Priority</InputLabel>
                        <Select value={priorityUpdate} label="Priority" onChange={(e) => setPriorityUpdate(e.target.value)}>
                          {Object.entries(PRIORITY_LABELS).map(([val, label]) => (
                            <MenuItem key={val} value={val}>{label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleUpdateTicket}
                        disabled={updateLoading || (statusUpdate === selectedTicket.status && priorityUpdate === selectedTicket.priority)}
                      >
                        {updateLoading ? <CircularProgress size={16} /> : 'Save'}
                      </Button>
                    </Box>
                  </Paper>

                  {/* Notes */}
                  <Typography variant="subtitle2" gutterBottom>Notes ({(selectedTicket.notes || []).length})</Typography>
                  {(selectedTicket.notes || []).length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>No notes yet.</Typography>
                  ) : (
                    <Box sx={{ mb: 2 }}>
                      {selectedTicket.notes.map((note, idx) => (
                        <Paper
                          key={idx}
                          variant="outlined"
                          sx={{
                            p: 1.5,
                            mb: 1,
                            bgcolor: note.type === 'internal' ? '#fff3e0' : '#e8f5e9',
                            borderLeft: `4px solid ${note.type === 'internal' ? '#ff9800' : '#4caf50'}`
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="caption" fontWeight={600}>
                              {note.authorName || 'Staff'} &middot; <Chip size="small" label={note.type} sx={{ height: 18, fontSize: '0.7rem' }} />
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {format(new Date(note.createdAt), 'dd MMM yyyy HH:mm:ss')}
                            </Typography>
                          </Box>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{note.text}</Typography>
                        </Paper>
                      ))}
                    </Box>
                  )}

                  {/* Add Note */}
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>Add Note</Typography>
                    <Box sx={{ mb: 1 }}>
                      <ToggleButtonGroup
                        value={noteType}
                        exclusive
                        onChange={(e, val) => { if (val) setNoteType(val); }}
                        size="small"
                      >
                        <ToggleButton value="internal">Internal (staff only)</ToggleButton>
                        <ToggleButton value="public">Public (visible to user)</ToggleButton>
                      </ToggleButtonGroup>
                    </Box>
                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      maxRows={6}
                      value={noteText}
                      onChange={(e) => setNoteText(e.target.value)}
                      placeholder="Write a note..."
                      size="small"
                      sx={{ mb: 1 }}
                    />
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleAddNote}
                      disabled={noteLoading || !noteText.trim()}
                    >
                      {noteLoading ? <CircularProgress size={16} /> : 'Add Note'}
                    </Button>
                  </Paper>
                  </Box>
                  )}
                </>
              )}
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button size="small" startIcon={<DownloadIcon />} onClick={() => downloadTicket('txt')}>
                  Download .txt
                </Button>
                <Button size="small" startIcon={<DownloadIcon />} onClick={() => downloadTicket('md')}>
                  Download .md
                </Button>
              </Box>
              <Button onClick={handleCloseDetail}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default SupportTicketsDashboard;
